import { cva, type VariantProps } from 'class-variance-authority';
import {
  CircleCheck,
  CircleDashed,
  CircleX,
  Clock,
  Info,
  Sparkles,
  type LucideIcon,
} from 'lucide-react';
import type { ComponentProps, ReactNode } from 'react';

import { cn } from './cn';

export type Tone = 'ok' | 'warn' | 'err' | 'info' | 'neutral' | 'accent';

export const badgeVariants = cva(
  'inline-flex h-[22px] max-w-full items-center gap-1 rounded-full px-2 text-xs font-medium whitespace-nowrap [&_svg]:size-3.5 [&_svg]:shrink-0',
  {
    variants: {
      tone: {
        ok: 'bg-ok-wash text-ok',
        warn: 'bg-warn-wash text-warn',
        err: 'bg-err-wash text-err',
        info: 'bg-info-wash text-info',
        neutral: 'bg-neutral-wash text-neutral',
        accent: 'bg-accent-wash text-accent-text',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export type BadgeProps = ComponentProps<'span'> & VariantProps<typeof badgeVariants>;

/** Etiqueta em wash + texto (Lume): par de cor com contraste AA medido no gate. */
export function Badge({ className, tone, ...props }: BadgeProps) {
  return (
    <span data-slot="badge" data-tone={tone ?? 'neutral'} className={cn(badgeVariants({ tone }), className)} {...props} />
  );
}

/** Ícone padrão por tom: o segundo canal da regra "nunca só cor" (U19). */
export const TONE_ICONS: Readonly<Record<Tone, LucideIcon>> = {
  ok: CircleCheck,
  warn: Clock,
  err: CircleX,
  info: Info,
  neutral: CircleDashed,
  accent: Sparkles,
};

export type StatusBadgeProps = Omit<BadgeProps, 'tone' | 'children'> & {
  readonly tone: Tone;
  readonly label: ReactNode;
  /** Troca o ícone do tom; `false` tira o ícone (só quando o rótulo já basta sem cor). */
  readonly icon?: LucideIcon | false;
};

/** Status com tom e ícone. O rótulo vem de quem chama, traduzido pelo valor do enum (U33). */
export function StatusBadge({ tone, label, icon, ...props }: StatusBadgeProps) {
  const Icon = icon === false ? null : (icon ?? TONE_ICONS[tone]);
  return (
    <Badge tone={tone} {...props}>
      {Icon ? <Icon aria-hidden /> : null}
      <span className="truncate">{label}</span>
    </Badge>
  );
}
